import { AxiosRequestConfig } from 'axios';
import { store } from '../../../web-legacy/appStore';
import {
  AlchemyResult,
  AlchemySolutionsCall,
  AlchemySolutionsCallType,
  AlchemySolutionsPayload,
  AlchemyThemeRoutingData,
  AuthInfo,
  AutoSuggestResult,
  AutoSuggestResultV2,
  ClientTokenRetrievalInfoModel,
  UserInfo,
} from '../../../web-legacy/models/apiModels';
import {
  AutoSuggestConstants,
  SourceApps,
  UnAuthBranchingQuery,
} from '../../../web-legacy/models/constants/constants';
import { IAppState } from '../../../web-legacy/models/reduxState';
import {
  getAimIdSelector,
  isAgaveSelector,
  isGetHelpSelector,
  isThirdPartySelector,
} from '../../../web-legacy/packages/app/selectors/contactUsSelector';
import { EmeraldConfigResponseModel } from '../../../web-legacy/packages/contactUs/models/configModels';
import {
  SearchRequestsRequestModel,
  SearchRequestsResponseModel,
} from '../../../web-legacy/packages/contactUs/models/contactUsModels';
import {
  FeedbackSurveyCategory,
  SurveyCheckBoxCategories,
} from '../../../web-legacy/packages/contactUs/models/feedbackModels';
import { ModalityResponseModel, Region, WaitTimeResponseReason } from '../../../web-legacy/packages/contactUs/models/modalityModels';
import {
  AttachmentData,
  ExtensionAttributeKeys,
  FreelanceRequest,
  RequestModality,
  RequestSeverity,
  UpdateContactsResponseModel,
} from '../../../web-legacy/packages/contactUs/models/requestModels';
import {
  ClientAuthProvider,
  FormUrlEncodedHeaders,
  addAlchemyPpeFlagToParam,
  addAppNamesToParam,
  addAppNamesToUrl,
  addExpGuidToParam,
  addExpGuidToUrl,
  addMarketToUrl,
  addQueryParamsToUrl,
  buildAuthToken,
  cleanQueryParams,
  deepCopyProperty,
  getAlchemyClientContext,
  getAlchemySolutionsUrlAndPayload,
  getAuthProviderFromReferrer,
  getAutoSuggestQueryParams,
  getAutoSuggestQueryParamsV2,
  getAutoSuggestWorkloadIdAppName,
  getContactSupportRequestHeaders,
  getFileMimeType,
  makeAADAuthHeader,
  makeMSAAuthHeader,
  updateCustomerContacts,
  updateDraftRequestMetadata,
  getQueryType,
  getAppInvoker
} from '../../../web-legacy/utilities';
import { NPSCampaign, SMCTicketing } from '../../models/constants/getHelpConstants';
import { appContext } from '../../models/getHelp/appContext';
import { EmeraldUserType } from '../../models/getHelp/emeraldUserType';
import { GetHelpSMCGetUserLicensesRequest } from '../../models/getHelp/getHelpSMCGetUserLicensesRequest';
import { GetHelpSMCGetUserLicensesResponse } from '../../models/getHelp/getHelpSMCGetUserLicensesResponse';
import { ICancelablePromise } from '../../models/httpModels';
import { getHelpLogger } from '../../utils/getHelpLogger';
import { captureContentUpdate, collectTelemetry, logCustomError } from '../../utils/oneDsUtil';
import { isGetHelpSourceApp } from '../../utils/statelessUtil';
import { httpUtils } from './httpUtils';
import { appendEmsSessionIdQueryParameter, inEmsSession } from '../../../common/utils/commonUtilities';
import { LogHint, LogType } from '../../models/emeraldLoggingRequest';
import { AlchemyFeedbackRequest } from '../../models/getHelp/AlchemyFeedbackRequest';
import { QueryType } from '../../models/queryType';
import { deeplinkPathSelector, isDeeplinkAndGetHelpSelector } from '../../../web-legacy/packages/app/selectors/selfHelpSelectors';

export const fetchAutoSuggestApi = async (query: string): Promise<AutoSuggestResult> => {
  const appState: IAppState = store.getState();
  const queryParams = getAutoSuggestQueryParams(
    appState.GlobalState.environment,
    appState.GlobalState.sourceApp,
    appState.GlobalState.namespace
  );
  queryParams.search = encodeURIComponent(query);

  const headers = { ...AutoSuggestConstants.headers };

  const queryString: string = Object.keys(queryParams)
    .map(key => `${key}=${queryParams[key]}`)
    .join('&');

  const destinationUrl: string = `${appState.ContactState.AutoSuggestState.autoSuggestBaseUrl}?${queryString}`;

  try {
    return await httpUtils.get<AutoSuggestResult>(destinationUrl, null, headers);
  } catch (error) {
    logCustomError(
      `fetchAutoSuggestApi - error caught, errorMessage: ${error.message}, statusCode: ${error.response?.status}`
    );
    throw error;
  }
};

export const fetchAutoSuggestApiV2 = async (query: string): Promise<AutoSuggestResultV2> => {
  const appState: IAppState = store.getState();
  const queryParams = getAutoSuggestQueryParamsV2();
  const appName: string = getAutoSuggestWorkloadIdAppName(
    appState.GlobalState.environment,
    appState.GlobalState.sourceApp,
    appState.GlobalState.namespace
  )['appName'];
  queryParams.query = encodeURIComponent(query);

  const queryString: string = Object.keys(queryParams)
    .map(key => `${key}=${queryParams[key]}`)
    .join('&');

  const destinationUrl: string = `${appState.ContactState.AutoSuggestState.autoSuggestBaseUrlV2.replace(
    '{0}',
    appName
  )}?${queryString}`;

  try {
    return await httpUtils.get<AutoSuggestResultV2>(destinationUrl);
  } catch (error) {
    logCustomError(
      `fetchAutoSuggestApiV2 - error caught, errorMessage: ${error.message}, statusCode: ${error.response?.status}`
    );
    throw error;
  }
};

export const userNoticesFetchApi = async (): Promise<AlchemyResult> => {
  const appState: IAppState = store.getState();
  const isGetHelp: boolean = isGetHelpSelector(appState);
  const userNoticeAlchemyQuery: string = appState.ContactState.SharedState.userNoticeAlchemyQuery;
  const userNoticeQuery: string = isGetHelp
    ? userNoticeAlchemyQuery
    : `${userNoticeAlchemyQuery}:${appState.GlobalState.market}`;

  const params: Record<string, string> = {};

  const alchemySolutionsCall: AlchemySolutionsCall = getAlchemySolutionsUrlAndPayload(
    store.getState,
    AlchemySolutionsCallType.AlchemyFetchNotifications,
    userNoticeQuery,
    appState.GlobalState.sourceApp,
    appState.GlobalState.tenantInfo,
    undefined,
    undefined,
    true
  );

  const destinationUrl: string = addMarketToUrl(alchemySolutionsCall.Url, appState.GlobalState.market);

  addAppNamesToParam(params, appState.GlobalState.appVer, appState.GlobalState.sourceApp);
  addExpGuidToParam(params);
  addAlchemyPpeFlagToParam(params);

  const config: AxiosRequestConfig = {
    params: params,
  };
  /*
   * This is the specific search query used to select a specific Alchemy bucket/rule.
   * Alchemy can return both articles and custom insights but only supports localization
   * for articles and uses the locale from the SOC request.  A rule can only use the
   * query sent in and nothing else from the request.  This means custom insights are
   * returned as is.  So to get localized messages I am appending the current locale to
   * the query so the rule can match on it, if necessary.  Of course, the insight text
   * must be localized.
   *
   * Once we switch to using only SMC articles, the code to add the locale can be removed.
   */
  try {
    const data: AlchemyResult = await httpUtils.post<AlchemyResult>(
      destinationUrl,
      alchemySolutionsCall.Payload,
      undefined,
      config
    );
    getHelpLogger.logAnnouncementReceived(data, alchemySolutionsCall.Payload.ClientContext.Flighting);
    return data;
  } catch (error) {
    logCustomError(
      `userNoticesFetchApi - error caught, errorMessage: ${error.message}, statusCode: ${error.response?.status}`
    );
    throw error;
  }
};

export const alchemyFetchApi = async (
  queryText?: string,
  isSpecialQuery?: boolean,
  bypassPreProcess?: boolean
): Promise<AlchemyResult> => {
  const appState: IAppState = store.getState();
  const isThirdParty: boolean = isThirdPartySelector(appState);
  const params: Record<string, string> = {};
  const isAutoSuggestCardSearch: boolean = appState.ContactState.SharedState.isAutoSuggestCardSearch;
  queryText = queryText || appState.ContactState.SharedState.contactSearchedQuery;
  const relatedSHCorrelationIds: string = isAutoSuggestCardSearch
    ? appState.ContactState.SelfHelpAlchemyState.relatedSHCorrelationIds
    : '';
  const sourceApp: SourceApps = appState.GlobalState.sourceApp;

  const alchemyType: AlchemySolutionsCallType =
    sourceApp === SourceApps.Proactive
      ? AlchemySolutionsCallType.AlchemyProactive
      : AlchemySolutionsCallType.AlchemySolutions;

  const alchemySolutionsCall: AlchemySolutionsCall = getAlchemySolutionsUrlAndPayload(
    store.getState,
    alchemyType,
    queryText,
    sourceApp,
    appState.GlobalState.tenantInfo,
    '',
    relatedSHCorrelationIds,
    isSpecialQuery,
    bypassPreProcess
  );

  const destinationUrl: string = addMarketToUrl(alchemySolutionsCall.Url, appState.GlobalState.market);

  addAppNamesToParam(params, appState.GlobalState.appVer, appState.GlobalState.sourceApp);
  addExpGuidToParam(params);
  addAlchemyPpeFlagToParam(params);

  const config: AxiosRequestConfig = {
    params: params,
  };

  getHelpLogger.logCallingHttpServiceThemeRouting();
  collectTelemetry({ ContentId: 'MadeAlchemyGetSolutionCall', Type: LogType.PageAction, Hint: LogHint.MadeAlchemyGetSolutionCall });

  try {
    return await httpUtils.post<AlchemyResult>(
      destinationUrl,
      alchemySolutionsCall.Payload,
      alchemySolutionsCall.RequestHeaders,
      config
    );
  } catch (error) {
    // Check soc2 here, will add to BRS later.
    if (!!sourceApp && sourceApp.toLowerCase() === SourceApps.SOC2) {
      const errMessage: string = error ? error.message : '';
      const errStack: string = error ? error.stack : '';
      let payloadApp: string | undefined = '';
      let payloadClientContext: boolean = false;
      let payloadRelatedSHCorrelationIds: string | undefined = '';
      let payloadText: string | undefined = '';

      if (!!alchemySolutionsCall.Payload) {
        const payload = alchemySolutionsCall.Payload;
        payloadApp = payload.App;
        payloadClientContext = !!payload.ClientContext;
        payloadRelatedSHCorrelationIds = payload.RelatedSHCorrelationIds;
        payloadText = payload.Text;
      }

      const logHandlerTags = {
        content: {
          contentId: 'AlchemyFetchError',
          contentName: 'AlchemyFetchError',
          formnm: 'maxFunnel',
          areaName: 'selfHelpAlchemy',
          errorMessage: errMessage,
          errorStack: errStack,
          url: destinationUrl,
          payloadApp: payloadApp,
          payloadClientContext: payloadClientContext,
          payloadRelatedSHCorrelationIds: payloadRelatedSHCorrelationIds,
          payloadText: payloadText,
        },
        behavior: 'CONTACT',
      };

      captureContentUpdate(logHandlerTags);
      throw error;
    }

    getHelpLogger.logAlchemyThemeRoutingDataReceivedError(error, destinationUrl);
  }
};

export const alchemyFetchUnAuthSchemaApi = async (): Promise<AlchemyResult> => {
  const appState: IAppState = store.getState();
  const sourceApp: SourceApps = appState.GlobalState.sourceApp;
  const alchemyAppName: string = appState.ContactState.ModalitySectionState.SMCUnauthExperienceAlchemyAppName;
  const alchemySolutionsCall: AlchemySolutionsCall = getAlchemySolutionsUrlAndPayload(
    store.getState,
    AlchemySolutionsCallType.AlchemyUnAuthBranching,
    UnAuthBranchingQuery,
    sourceApp,
    appState.GlobalState.tenantInfo,
    alchemyAppName,
    '',
    isGetHelpSourceApp(sourceApp),
    true
  );

  const destinationUrl: string = addMarketToUrl(alchemySolutionsCall.Url, appState.GlobalState.market);
  const params: Record<string, string> = {};

  addAppNamesToParam(params, appState.GlobalState.appVer, sourceApp);
  addExpGuidToParam(params);
  addAlchemyPpeFlagToParam(params);

  const config: AxiosRequestConfig = {
    params: params,
  };

  try {
    return await httpUtils.post<AlchemyResult>(
      destinationUrl,
      alchemySolutionsCall.Payload,
      alchemySolutionsCall.RequestHeaders,
      config
    );
  } catch (error) {
    // Check soc2 here, will add to BRS later.
    if (!!sourceApp && sourceApp.toLowerCase() === SourceApps.SOC2) {
      const errMessage: string = error ? error.message : '';
      const errStack: string = error ? error.stack : '';
      let payloadApp: string | undefined = '';
      let payloadClientContext: boolean = false;
      let payloadRelatedSHCorrelationIds: string | undefined = '';
      let payloadText: string | undefined = '';

      if (!!alchemySolutionsCall.Payload) {
        const payload: AlchemySolutionsPayload = alchemySolutionsCall.Payload;
        payloadApp = payload.App;
        payloadClientContext = !!payload.ClientContext;
        payloadRelatedSHCorrelationIds = payload.RelatedSHCorrelationIds;
        payloadText = payload.Text;
      }

      const logHandlerTags = {
        content: {
          contentId: 'AlchemyFetchUnAuthSchemaApiError',
          contentName: 'AlchemyFetchUnAuthSchemaApiError',
          formnm: 'maxFunnel',
          areaName: 'selfHelpAlchemy',
          errorMessage: errMessage,
          errorStack: errStack,
          url: destinationUrl,
          payloadApp: payloadApp,
          payloadClientContext: payloadClientContext,
          payloadRelatedSHCorrelationIds: payloadRelatedSHCorrelationIds,
          payloadText: payloadText,
        },
        behavior: 'CONTACT',
      };

      captureContentUpdate(logHandlerTags);
      throw error;
    }
  }
};

/**
 * Log Alchemy feedback for bucket performance
 * @param {AlchemyFeedbackRequest} feedback the Alchemy feedback for bucket performance
 */
export const postAlchemyFeedbackAsync = async (feedback: AlchemyFeedbackRequest): Promise<void> => {
  const appState: IAppState = store.getState();
  const isDeepLinkQuery: boolean = appState.ContactState.SharedState.isDeepLinkQuery || isDeeplinkAndGetHelpSelector(appState);
  const IsAutoSuggestQuery: boolean = appState.ContactState.AutoSuggestState.isAutoSuggestQuery;
  const sourceApp: SourceApps = appState.GlobalState.sourceApp;
  const isGetHelp: boolean = isGetHelpSelector(appState);
  const url = isGetHelp
    ? `${appContext.environment.baseUrl}/api/v1/apps/gethelp/feedback`
    : addMarketToUrl(`api/feedback/feedback`, appState.GlobalState.market);
  const appInvoker = isDeepLinkQuery && isGetHelp ? getAppInvoker(deeplinkPathSelector(appState)) : '';

  const request: AlchemyFeedbackRequest = {
    ...feedback,
    UserType: appContext.emeraldUserType,
    QueryType: getQueryType(isDeepLinkQuery, IsAutoSuggestQuery),
    AimId: getAimIdSelector(appState),
    AppInvoker: appInvoker,
    SourceApp: sourceApp,
  };

  const logHandlerTags = {
    content: {
      contentId: 'PostAlchemyFeedback',
      contentName: 'PostAlchemyFeedback',
      areaName: 'contactUs',
      formnm: 'maxFunnel',
      sessionId: httpUtils.getEmsSessionId(),
      userType: request.UserType,
      queryType: request.QueryType,
      aimId: request.AimId,
      appInvoker: request.AppInvoker,
      sourceApp: request.SourceApp,
    },
    behaviour: 'CONTACT',
  };
  captureContentUpdate(logHandlerTags);

  try {
    await httpUtils.post<AlchemyFeedbackRequest>(url, request);
  } catch (error) {
    // Do nothing if the logging endpoint is down.
  }
};

export const userInfoFetchApi = async (): Promise<UserInfo> => {
  const appState: IAppState = store.getState();
  const isGetHelp: boolean = isGetHelpSourceApp(appState.GlobalState.sourceApp);
  const defaultRequestHeaders: Record<string, string> = {
    'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
  };
  const cacheBuster: string = new Date().getTime().toString();
  const params: Record<string, string> = {
    cacheBuster: cacheBuster,
  };

  addAppNamesToParam(params, appState.GlobalState.appVer, appState.GlobalState.sourceApp);

  // adding bearer token for Teams Support app, Teams Admin app and Office.com Admin app for authentication since we are getting token from the host apps
  if (
    !!appState.GlobalState.sourceApp &&
    (appState.GlobalState.sourceApp === SourceApps.Teams ||
      appState.GlobalState.sourceApp === SourceApps.Teams2 ||
      appState.GlobalState.sourceApp === SourceApps.TeamsAdmin ||
      appState.GlobalState.sourceApp === SourceApps.TeamsAdmin2 ||
      appState.GlobalState.sourceApp === SourceApps.OfficeDotCom ||
      appState.GlobalState.sourceApp === SourceApps.OutlookAdmin ||
      appState.GlobalState.sourceApp === SourceApps.LightHouse ||
      appState.GlobalState.sourceApp === SourceApps.OneDriveWeb ||
      appState.GlobalState.sourceApp === SourceApps.SurfaceApp ||
      appState.GlobalState.sourceApp === SourceApps.SurfaceApp2 ||
      isGetHelp)
  ) {
    const authInfo: AuthInfo = appState.ContactState.ModalitySectionState.authInfo;
    if (!!authInfo && !!authInfo.token) {
      defaultRequestHeaders['Authorization'] = buildAuthToken(authInfo.provider, authInfo.token);
    }
  }

  addExpGuidToParam(params);
  const config: AxiosRequestConfig = {
    params: params,
  };

  try {
    if (isGetHelp) {
      return await httpUtils.get<UserInfo>(
        addMarketToUrl('/api/contactus/v1/GetUserInfo', appState.GlobalState.market),
        config,
        defaultRequestHeaders
      );
    } else {
      let userInfoApiPath = inEmsSession() ? '/ems/auth/UserInfo' : '/home/UserInfo';
      return await httpUtils.post<UserInfo>(
        userInfoApiPath,
        '__RequestVerificationToken=' +
        encodeURIComponent(store.getState().ContactState.SharedState.requestVerificationToken),
        defaultRequestHeaders,
        config
      );
    }
  } catch (error) {
    logCustomError(
      `userInfoFetchApi - error caught, errorMessage: ${error.message}, statusCode: ${error.response?.status}`
    );
    throw error;
  }
};

export const redirectForSignInLinkApi = async (): Promise<string> => {
  const appState: IAppState = store.getState();
  let signInPath = inEmsSession() ? '/ems/auth/RedirectForSignInLink' : '/home/RedirectForSignInLink';
  let destinationUrl: string = addExpGuidToUrl(signInPath);
  let returnUriValue = inEmsSession() ? '/ems/auth/JSAuthEventNotification' : '/home/JSAuthEventNotification';

  if (appState.GlobalState.sourceApp === SourceApps.SurfaceApp || appState.GlobalState.sourceApp === SourceApps.SurfaceApp2) {
    returnUriValue = `/home/contact?linkquery=${appState.ContactState.SharedState.contactSearchedQuery}`;

    if (!!appState.GlobalState.theme && appState.GlobalState.theme.toLowerCase() === 'darkgray') {
      returnUriValue += '&theme=dark';
    }
  }

  destinationUrl = addAppNamesToUrl(destinationUrl, appState.GlobalState.appVer, appState.GlobalState.sourceApp);
  returnUriValue = addAppNamesToUrl(returnUriValue, appState.GlobalState.appVer, appState.GlobalState.sourceApp);
  destinationUrl = addQueryParamsToUrl(destinationUrl, {
    'returnUriValue': encodeURIComponent(
      window.location.protocol + '//' + window.location.host + addExpGuidToUrl(returnUriValue)
    ),
    'authProvider': isAgaveSelector(appState) ? getAuthProviderFromReferrer()?.toString() : '',
  });
  destinationUrl = addQueryParamsToUrl(destinationUrl, { 'market': appState.GlobalState.market });

  try {
    return await httpUtils.get<string>(destinationUrl);
  } catch (error) {
    logCustomError(
      `redirectForSignInLinkApi - error caught, errorMessage: ${error.message}, statusCode: ${error.response?.status}`
    );
    throw error;
  }
};

export const availableModalitiesV2FetchApi = async (): Promise<ModalityResponseModel> => {
  const appState: IAppState = store.getState();
  const requestHeaders: Record<string, string> = getContactSupportRequestHeaders(store.getState);
  const params: Record<string, string> = {};
  const preferredLocale: string = appState.ContactState.ModalitySectionState.preferredLocale;
  const destinationUrl: string = addMarketToUrl(
    '/api/contactus/v2/GetAvailableModalities',
    preferredLocale ? preferredLocale : appState.GlobalState.market
  );
  let draftRequest: FreelanceRequest = appState.ContactState.ModalitySectionState.draftRequest;

  if (isGetHelpSelector(appState)) {
    const appNameExtensionAttribute: string =
      appContext.emeraldUserType === EmeraldUserType.Commercial
        ? SMCTicketing.AppNameForEmeraldCommercial
        : SMCTicketing.AppNameForEmeraldConsumer;
    draftRequest = deepCopyProperty(draftRequest);
    draftRequest.ExtensionAttributes[ExtensionAttributeKeys.AppName] = appNameExtensionAttribute;
    draftRequest.ExtensionAttributes[ExtensionAttributeKeys.TimezoneOffsetInMinutes] =
      new Date().getTimezoneOffset()?.toString() ?? '';
    draftRequest.SupportAreaId = '';
    draftRequest.Severity = RequestSeverity.None;
  }

  addAppNamesToParam(params, appState.GlobalState.appVer, appState.GlobalState.sourceApp);
  addExpGuidToParam(params);
  const config: AxiosRequestConfig = {
    params: params,
  };

  let response: ModalityResponseModel;
  try {
    getHelpLogger.logCallingModalitiesV2Wrapper();
    collectTelemetry({ ContentId: 'MadeGetAvailableModalitiesCall', Type: LogType.PageAction, Hint: LogHint.MadeGetAvailableModalitiesCall });
    response = await httpUtils.post<ModalityResponseModel>(destinationUrl, draftRequest, requestHeaders, config);
    return response;
  } catch (error) {
    getHelpLogger.logGetAvailableModalitiesV2Error(error, destinationUrl);
    collectTelemetry({
      ContentId: 'GetAvailableModalitiesCallFailed', Type: LogType.ContentUpdate, Hint: LogHint.GetAvailableModalitiesCallFailed,
      Details: { 'errorMessage': error.message ?? '', 'statusCode': error.response?.status ?? '' }
    })
    logCustomError(
      `availableModalitiesV2FetchApi - error caught, errorMessage: ${error.message}, statusCode: ${error.response?.status}`
    );
    throw error;
  } finally {
    getHelpLogger.logGetModalitiesV2CallResult(draftRequest, response, requestHeaders['X-CorrelationId']);
    collectTelemetry({
      ContentId: 'ReveivedAvailableModalities', Type: LogType.ContentUpdate, Hint: LogHint.ReveivedAvailableModalities,
      Details: {
        'agentModalitiesIBH': response?.AgentModalities?.Modalities?.filter((modality) => modality.Reason == WaitTimeResponseReason.None)?.map((modality) => RequestModality[modality.Modality]) ?? '',
        'agentModalitiesOOBH': response?.AgentModalities?.Modalities?.filter((modality) => modality.Reason != WaitTimeResponseReason.None)?.map((modality) => RequestModality[modality.Modality]) ?? '',
        'nonAgentModalities': response?.NonAgentModalities?.Modalities?.map((modality) => modality.Id) ?? ''
      }
    });
  }
};

export const getRegionInfoApi = async (): Promise<Region[]> => {
  const appState: IAppState = store.getState();
  const params: Record<string, string> = {};
  const destinationUrl: string = addMarketToUrl('/api/contactus/v1/GetRegionInfo', appState.GlobalState.market);
  addAppNamesToParam(params, appState.GlobalState.appVer, appState.GlobalState.sourceApp);
  addExpGuidToParam(params);
  const config: AxiosRequestConfig = {
    params: params,
  };

  try {
    return await httpUtils.get<Region[]>(destinationUrl, config);
  } catch (error) {
    logCustomError(
      `getRegionInfoApi - error caught, errorMessage: ${error.message}, statusCode: ${error.response?.status}`
    );
    throw error;
  }
};

export const createRequestApi = async (): Promise<FreelanceRequest> => {
  const appState: IAppState = store.getState();
  const requestHeaders: Record<string, string> = getContactSupportRequestHeaders(store.getState);
  const params: Record<string, string> = {};
  const preferredLocale: string = appState.ContactState.ModalitySectionState.preferredLocale;
  const destinationUrl: string = addMarketToUrl(
    '/api/contactus/v1/addrequest',
    preferredLocale ? preferredLocale : appState.GlobalState.market
  );
  addAppNamesToParam(params, appState.GlobalState.appVer, appState.GlobalState.sourceApp);
  addExpGuidToParam(params);
  const config: AxiosRequestConfig = {
    params: params,
  };

  // Setup the draft request
  const draftRequest: FreelanceRequest = updateDraftRequestMetadata(store.getState);
  updateCustomerContacts(draftRequest);

  if (store.getState().GlobalState.isAddClientContextEnabled){
    draftRequest.ClientContext = getAlchemyClientContext(store.getState);
  }

  getHelpLogger.logCallingTicketCreationWrapper();
  collectTelemetry({ ContentId: 'MadeTicketCreationCall', Type: LogType.PageAction, Hint: LogHint.MadeTicketCreationCall });

  try {
    const data: FreelanceRequest = await httpUtils.post<FreelanceRequest>(
      destinationUrl,
      draftRequest,
      requestHeaders,
      config
    );
    getHelpLogger.logSupportComplete(
      data.ParatureTicketNumber,
      data.State,
      data.RequestId,
      draftRequest.ExtensionAttributes[ExtensionAttributeKeys.WaitTime]
    );
    collectTelemetry({
      ContentId: 'TicketCreated', Type: LogType.ContentUpdate, Hint: LogHint.TicketCreated,
      Details: { 'ticketNumber': data.ParatureTicketNumber, 'state': data.State, 'requestId': data.RequestId }
    });
    return data;
  } catch (error) {
    getHelpLogger.logSupportError(`createRequestApi - ${error.message ?? ''}`);
    collectTelemetry({
      ContentId: 'TicketCreationFailed', Type: LogType.ContentUpdate, Hint: LogHint.TicketCreationFailed,
      Details: { 'errorMessage': error.message ?? '', 'statusCode': error.response?.status ?? '' }
    });
    logCustomError(
      `createRequestApi - error caught, errorMessage: ${error.message}, statusCode: ${error.response?.status}`
    );
    throw error;
  }
};

export const searchRequestsApi = async (): Promise<SearchRequestsResponseModel> => {
  const appState: IAppState = store.getState();
  const requestHeaders: Record<string, string> = getContactSupportRequestHeaders(store.getState);
  const searchRequestsRequestModel: SearchRequestsRequestModel = {
    ...appState.ContactState.RequestHistoryState.searchRequestsRequestModel,
  };
  const params: Record<string, string> = {};
  const destinationUrl: string = addMarketToUrl('/api/contactus/v1/SearchRequests', appState.GlobalState.market);
  addAppNamesToParam(params, appState.GlobalState.appVer, appState.GlobalState.sourceApp);
  addExpGuidToParam(params);
  const config: AxiosRequestConfig = {
    params: params,
  };

  try {
    return await httpUtils.post<SearchRequestsResponseModel>(
      destinationUrl,
      searchRequestsRequestModel,
      requestHeaders,
      config
    );
  } catch (error) {
    logCustomError(
      `searchRequestsApi - error caught, errorMessage: ${error.message}, statusCode: ${error.response?.status}`
    );
    throw error;
  }
};

export const getRequestApi = async (requestId: string): Promise<FreelanceRequest> => {
  const appState: IAppState = store.getState();
  const requestHeaders: Record<string, string> = getContactSupportRequestHeaders(store.getState);
  const params: Record<string, string> = {
    requestId: requestId,
  };
  const destinationUrl: string = addMarketToUrl('/api/contactus/v1/GetRequest', appState.GlobalState.market);
  addAppNamesToParam(params, appState.GlobalState.appVer, appState.GlobalState.sourceApp);
  addExpGuidToParam(params);
  const config: AxiosRequestConfig = {
    params: cleanQueryParams(params),
  };

  try {
    return await httpUtils.get<FreelanceRequest>(destinationUrl, config, requestHeaders);
  } catch (error) {
    logCustomError(
      `getRequestApi - error caught, errorMessage: ${error.message}, statusCode: ${error.response?.status}`
    );
    throw error;
  }
};

export const updateAttachmentsApi = async (): Promise<FreelanceRequest> => {
  const appState: IAppState = store.getState();
  const requestHeaders: Record<string, string> = getContactSupportRequestHeaders(store.getState);
  const params: Record<string, string> = {};
  const destinationUrl: string = addMarketToUrl('/api/contactus/v1/UpdateAttachments', appState.GlobalState.market);
  addAppNamesToParam(params, appState.GlobalState.appVer, appState.GlobalState.sourceApp);
  addExpGuidToParam(params);
  const config: AxiosRequestConfig = {
    params: params,
  };
  const dirtyRequest: FreelanceRequest = { ...appState.ContactState.RequestDetailsState.dirtyRequest };

  try {
    return await httpUtils.post<FreelanceRequest>(destinationUrl, dirtyRequest, requestHeaders, config);
  } catch (error) {
    logCustomError(
      `updateAttachmentsApi - error caught, errorMessage: ${error.message}, statusCode: ${error.response?.status}`
    );
    throw error;
  }
};

export const addNoteApi = async (): Promise<FreelanceRequest> => {
  const appState: IAppState = store.getState();
  const requestHeaders: Record<string, string> = getContactSupportRequestHeaders(store.getState);
  const params: Record<string, string> = {};
  const destinationUrl: string = addMarketToUrl('/api/contactus/v1/AddNote', appState.GlobalState.market);
  addAppNamesToParam(params, appState.GlobalState.appVer, appState.GlobalState.sourceApp);
  addExpGuidToParam(params);
  const config: AxiosRequestConfig = {
    params: params,
  };
  const dirtyRequest: FreelanceRequest = { ...appState.ContactState.RequestDetailsState.dirtyRequest };

  try {
    return await httpUtils.post<FreelanceRequest>(destinationUrl, dirtyRequest, requestHeaders, config);
  } catch (error) {
    logCustomError(`addNoteApi - error caught, errorMessage: ${error.message}, statusCode: ${error.response?.status}`);
    throw error;
  }
};

export const updateContactsApi = async (): Promise<UpdateContactsResponseModel> => {
  const appState: IAppState = store.getState();
  const requestHeaders: Record<string, string> = getContactSupportRequestHeaders(store.getState);
  const params: Record<string, string> = {};
  const destinationUrl: string = addMarketToUrl('/api/contactus/v1/UpdateContacts', appState.GlobalState.market);
  addAppNamesToParam(params, appState.GlobalState.appVer, appState.GlobalState.sourceApp);
  addExpGuidToParam(params);
  const config: AxiosRequestConfig = {
    params: params,
  };
  const dirtyRequest: FreelanceRequest = { ...appState.ContactState.RequestDetailsState.dirtyRequest };
  updateCustomerContacts(dirtyRequest);
  const updateContactsRequestModel = {
    CustomerContacts: dirtyRequest.CustomerContacts,
    RequestId: dirtyRequest.RequestId,
    TwoDigitCountryCode: dirtyRequest.TwoDigitCountryCode,
    TenantId: dirtyRequest.TenantId,
    UserPuid: dirtyRequest.UserPuid,
  };

  try {
    return await httpUtils.post<UpdateContactsResponseModel>(
      destinationUrl,
      updateContactsRequestModel,
      requestHeaders,
      config
    );
  } catch (error) {
    logCustomError(
      `updateContactsApi - error caught, errorMessage: ${error.message}, statusCode: ${error.response?.status}`
    );
    throw error;
  }
};

export const getInfoForClientTokenRetrievalApi = async (): Promise<ClientTokenRetrievalInfoModel> => {
  const appState: IAppState = store.getState();
  const tokenInfoModel: ClientTokenRetrievalInfoModel =
    store.getState().ContactState.ModalitySectionState.clientTokenRetrievalInfoModel;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (tokenInfoModel !== null) {
    headers['User-Context'] = tokenInfoModel.UserContext;
  }
  const params: Record<string, string> = {};
  const destinationUrl = inEmsSession() ? '/ems/auth/GetInfoForClientTokenRetrieval' : '/home/GetInfoForClientTokenRetrieval';
  addExpGuidToParam(params);
  const config: AxiosRequestConfig = {
    params: params,
  };

  addAppNamesToParam(params, appState.GlobalState.appVer, appState.GlobalState.sourceApp);

  try {
    return await httpUtils.get<ClientTokenRetrievalInfoModel>(destinationUrl, config, headers);
  } catch (error) {
    logCustomError(
      `getInfoForClientTokenRetrievalApi - error caught, errorMessage: ${error.message}, statusCode: ${error.response?.status}`
    );
    throw error;
  }
};

export const authenticateUserApi = async (authInfo: AuthInfo): Promise<void> => {
  const appState: IAppState = store.getState();
  const params: Record<string, string> = {};
  const tokenInfoModel: ClientTokenRetrievalInfoModel =
    appState.ContactState.ModalitySectionState.clientTokenRetrievalInfoModel;
  const headers: Record<string, string> = {
    'User-Context': tokenInfoModel.UserContext,
    'Content-Type': FormUrlEncodedHeaders['Content-Type'],
  };
  const loginProvider: string = authInfo.provider;
  const authHeader: string =
    loginProvider === ClientAuthProvider.AAD ? makeAADAuthHeader(authInfo.token) : makeMSAAuthHeader(authInfo.token);

  const parsedUrl: URL = new URL(appState.ContactState.ModalitySectionState.backgroundAuthUri);
  let authUrl: string;
  if (loginProvider === ClientAuthProvider.AAD) {
    authUrl = parsedUrl.searchParams.get('state');
  } else {
    const newUrl: URL = new URL(parsedUrl.searchParams.get('wreply') || '');
    authUrl = newUrl.searchParams.get('url');
  }
  // tslint:disable-next-line:no-string-literal
  headers['Authorization'] = authHeader;
  addExpGuidToParam(params);
  addAppNamesToParam(params, appState.GlobalState.appVer, appState.GlobalState.sourceApp);

  const config: AxiosRequestConfig = {
    params: params,
  };

  try {
    return await httpUtils.get<void>(authUrl, config, headers);
  } catch (error) {
    logCustomError(
      `authenticateUserApi - error caught, errorMessage: ${error.message}, statusCode: ${error.response?.status}`
    );
    throw error;
  }
};

export const fetchBackgroundAuthUriApi = async (provider: string): Promise<string> => {
  const appState: IAppState = store.getState();
  const params: Record<string, string> = {
    authProvider: provider,
  };
  const destinationUrl: string = inEmsSession() ? `/ems/auth/GetBackgroundAuthUri` : `/home/GetBackgroundAuthUri`;
  addExpGuidToParam(params);
  addAppNamesToParam(params, appState.GlobalState.appVer, appState.GlobalState.sourceApp);

  const config: AxiosRequestConfig = {
    params: params,
  };

  try {
    return await httpUtils.get<string>(destinationUrl, config, { 'Content-Type': 'application/json' });
  } catch (error) {
    logCustomError(
      `fetchBackgroundAuthUriApi - error caught, errorMessage: ${error.message}, statusCode: ${error.response?.status}`
    );
    throw error;
  }
};

export const userInfoFetchAfterClientSignInApi = async (): Promise<UserInfo> => {
  const headers: Record<string, string> = { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' };
  const appState: IAppState = store.getState();
  const cacheBuster: string = new Date().getTime().toString();
  const tokenInfoModel: ClientTokenRetrievalInfoModel =
    appState.ContactState.ModalitySectionState.clientTokenRetrievalInfoModel;
  if (tokenInfoModel.UserContext !== null) {
    headers['User-Context'] = tokenInfoModel.UserContext;
  }
  const params: Record<string, string> = {
    cacheBuster: cacheBuster,
  };
  const destinationUrl: string = inEmsSession() ? `/ems/auth/UserInfo` : `/home/UserInfo`;
  addExpGuidToParam(params);
  addAppNamesToParam(params, appState.GlobalState.appVer, appState.GlobalState.sourceApp);

  const config: AxiosRequestConfig = {
    params: params,
  };

  try {
    return await httpUtils.post<UserInfo>(
      destinationUrl,
      '__RequestVerificationToken=' + encodeURIComponent(appState.ContactState.SharedState.requestVerificationToken),
      headers,
      config
    );
  } catch (error) {
    logCustomError(
      `userInfoFetchAfterClientSignInApi - error caught, errorMessage: ${error.message}, statusCode: ${error.response?.status}`
    );
    throw error;
  }
};

export const getCSRFFormTokenRetrievalApi = async (
  namespace: string,
  version: string,
  headers?: Record<string, string>
): Promise<string> => {
  const appState: IAppState = store.getState();
  const cacheBuster: string = new Date().getTime().toString();
  const params: Record<string, string> = {
    cacheBuster: cacheBuster,
    NS: namespace,
    Ver: version,
  };
  const destinationUrl: string = inEmsSession() ? `/ems/auth/GetCSRFFormToken` : `/home/GetCSRFFormToken`;
  addExpGuidToParam(params);
  addAppNamesToParam(params, appState.GlobalState.appVer, appState.GlobalState.sourceApp);

  const config: AxiosRequestConfig = {
    params: params,
  };
  // Todo: Brook, validate headers, was being sent from action
  try {
    return await httpUtils.get<string>(destinationUrl, config, headers);
  } catch (error) {
    logCustomError(
      `getCSRFFormTokenRetrievalApi - error caught, errorMessage: ${error.message}, statusCode: ${error.response?.status}`
    );
    throw error;
  }
};

export const getUploadAttachmentTokenApi = async (workspaceId: string, requestId: string): Promise<string> => {
  const appState: IAppState = store.getState();
  const requestHeaders: Record<string, string> = getContactSupportRequestHeaders(store.getState);
  const params: Record<string, string> = {
    associationId: workspaceId,
    requestId,
  };
  const destinationUrl: string = addMarketToUrl(
    '/api/contactus/v1/GetUploadAttachmentToken',
    appState.GlobalState.market
  );
  addExpGuidToParam(params);
  addAppNamesToParam(params, appState.GlobalState.appVer, appState.GlobalState.sourceApp);
  const config: AxiosRequestConfig = {
    params: cleanQueryParams(params),
  };

  try {
    return await httpUtils.get<string>(destinationUrl, config, requestHeaders);
  } catch (error) {
    logCustomError(
      `getUploadAttachmentTokenApi - error caught, errorMessage: ${error.message}, statusCode: ${error.response?.status}`
    );
    throw error;
  }
};

export const uploadFileMetadataApi = (
  token: string,
  chunkSizeInMB: number,
  doc: File,
  requestId: string
): ICancelablePromise<AttachmentData> => {
  const appState: IAppState = store.getState();
  const requestHeaders: Record<string, string> = getContactSupportRequestHeaders(store.getState);
  const params: Record<string, string> = {};
  const destinationUrl: string = addMarketToUrl('/api/contactus/v1/UploadFileMetaData', appState.GlobalState.market);
  addExpGuidToParam(params);
  addAppNamesToParam(params, appState.GlobalState.appVer, appState.GlobalState.sourceApp);
  const config: AxiosRequestConfig = {
    params: params,
  };

  const body = {
    'Token': token,
    'FileName': doc.name,
    'ContentType': getFileMimeType(doc.name, doc.type),
    'FileSizeInB': doc.size,
    'ChunkSizeInMB': chunkSizeInMB,
    'RequestId': requestId,
  };

  return httpUtils.post<AttachmentData>(destinationUrl, body, requestHeaders, config);
};

export const uploadFileChunkApi = (
  attachmentData: AttachmentData,
  token: string,
  requestId: string,
  form: FormData,
  chunkIndex: number
): ICancelablePromise<void> => {
  // Preparation Work
  const appState: IAppState = store.getState();
  const requestHeaders: Record<string, string> = getContactSupportRequestHeaders(store.getState);
  // Do not set Content-Type header manually, it will automatically be set to multipart/form-data; boundary=----WebKit
  delete requestHeaders['Content-Type'];
  const params: Record<string, string> = {
    token: token,
    fileName: attachmentData.Location,
    attachmentId: attachmentData.CwsId,
    requestId: requestId,
  };
  const currentChunkUrl: string = addMarketToUrl('/api/contactus/v1/UploadFileChunk', appState.GlobalState.market);
  addExpGuidToParam(params);
  addAppNamesToParam(params, appState.GlobalState.appVer, appState.GlobalState.sourceApp);
  params['chunkIndex'] = chunkIndex.toString();
  const config: AxiosRequestConfig = {
    params: cleanQueryParams(params),
  };

  return httpUtils.post<void>(currentChunkUrl, form, requestHeaders, config);
};

export const getDownloadAttachmentTokenApi = async (cwsId: string, requestId: string): Promise<string> => {
  const appState: IAppState = store.getState();
  const requestHeaders: Record<string, string> = getContactSupportRequestHeaders(store.getState);
  const params: Record<string, string> = {
    cwsId: cwsId,
    requestId: requestId,
  };
  const destinationUrl: string = addMarketToUrl(
    '/api/contactus/v1/GetDownloadAttachmentToken',
    appState.GlobalState.market
  );
  addAppNamesToParam(params, appState.GlobalState.appVer, appState.GlobalState.sourceApp);
  addExpGuidToParam(params);
  const config: AxiosRequestConfig = {
    params: cleanQueryParams(params),
  };

  try {
    const results: { status: number; data: string } = await httpUtils.get<{ status: number; data: string }>(
      destinationUrl,
      config,
      requestHeaders,
      true
    );
    let downloadUrl: string =
      results.status === 201
        ? addMarketToUrl(
          `/api/contactus/v1/DownloadAttachment?cwsId=${cwsId}&token=${results.data}&databoundary=${requestHeaders['X-Ms-DataBoundary']}`,
          appState.GlobalState.market
        )
        : `${appState.ContactState.AttachmentsState.attachmentsConfig.FtsBaseUrl}/v1/services/download?attachmentCwsId=${cwsId}&token=${results.data}`;
    downloadUrl = appendEmsSessionIdQueryParameter(downloadUrl);
    // Open the download URL in a new tab
    // eslint-disable-next-line
    window.open(downloadUrl, '_self');

    return results.data;
  } catch (error) {
    logCustomError(
      `getDownloadAttachmentTokenApi - error caught, errorMessage: ${error.message}, statusCode: ${error.response?.status}`
    );
    throw error;
  }
};

export const getAlchemyThemeRoutingDataApi = async (): Promise<AlchemyThemeRoutingData> => {
  const appState: IAppState = store.getState();
  const requestHeaders: Record<string, string> = getContactSupportRequestHeaders(store.getState);
  const params: Record<string, string> = {};
  const destinationUrl: string = addMarketToUrl(
    '/api/contactus/v1/GetAlchemyThemeRoutingData',
    appState.GlobalState.market
  );
  addAppNamesToParam(params, appState.GlobalState.appVer, appState.GlobalState.sourceApp);
  addExpGuidToParam(params);
  const config: AxiosRequestConfig = {
    params: cleanQueryParams(params),
  };

  try {
    const results: { status: number; data: AlchemyThemeRoutingData } = await httpUtils.get<{ status: number; data: AlchemyThemeRoutingData }>(
      destinationUrl,
      config,
      requestHeaders,
      true
    );
    return results.data;
  } catch (error) {
    logCustomError(
      `getAlchemyThemeRoutingDataApi - error caught, errorMessage: ${error.message}, statusCode: ${error.response?.status}`
    );
    throw error;
  }
};

export const submitFeedbackApi = async (
  requestData: FreelanceRequest,
  checkBoxes: SurveyCheckBoxCategories
): Promise<boolean> => {
  const appState: IAppState = store.getState();
  const requestHeaders: Record<string, string> = getContactSupportRequestHeaders(store.getState);
  const request: FreelanceRequest = { ...requestData };
  const surveyCategories: FeedbackSurveyCategory[] = [];

  Object.entries(checkBoxes).forEach(([key, value]) => {
    if (value) {
      surveyCategories.push(FeedbackSurveyCategory[key]);
    }
  });

  request.SurveyCategories = surveyCategories;
  const destinationUrl: string = addMarketToUrl('/api/contactus/v1/SubmitFeedback', appState.GlobalState.market);
  const params: Record<string, string> = {};
  addExpGuidToParam(params);
  addAppNamesToParam(params, appState.GlobalState.appVer, appState.GlobalState.sourceApp);
  const config: AxiosRequestConfig = {
    params: params,
  };

  try {
    return await httpUtils.post<boolean>(destinationUrl, request, requestHeaders, config);
  } catch (error) {
    logCustomError(
      `submitFeedbackApi - error caught, errorMessage: ${error.message}, statusCode: ${error.response?.status}`
    );
    throw error;
  }
};

export const LogBundleLoadSuccessApi = async (response: EmeraldConfigResponseModel): Promise<void> => {
  const appState: IAppState = store.getState();
  const requestHeaders: Record<string, string> = getContactSupportRequestHeaders(store.getState);
  const params: Record<string, string> = {};
  const destinationUrl: string = addMarketToUrl('/api/contactus/v1/LogBundleLoadSuccess', appState.GlobalState.market);
  // WAC gets appVer from GetEmeraldConfig so get it from response if its not set in redux yet due to race condition. InApp gets from redux
  const appVer: string = appState.GlobalState.appVer || response.AppVer;
  addAppNamesToParam(params, appVer, appState.GlobalState.sourceApp);
  addExpGuidToParam(params);
  const config: AxiosRequestConfig = {
    params: params,
  };

  try {
    return await httpUtils.get<void>(destinationUrl, config, requestHeaders);
  } catch (error) {
    logCustomError(
      `LogBundleLoadSuccessApi - error caught, errorMessage: ${error.message}, statusCode: ${error.response?.status}`
    );
    throw error;
  }
};

export const getUserLicensesApi = async (transactionId: string): Promise<GetHelpSMCGetUserLicensesResponse> => {
  const appState: IAppState = store.getState();
  const market: string = appState.GlobalState.market;
  const sourceApp: SourceApps = appState.GlobalState.sourceApp;
  const destinationUrl: string = addMarketToUrl(NPSCampaign.GetUserLicensesUrl, market);
  let sessionId: string = '';
  if (isGetHelpSelector(appState)) {
    sessionId = appContext.sessionId;
  }
  const data: GetHelpSMCGetUserLicensesRequest = {
    App: sourceApp,
    TransactionId: transactionId,
    CorrelationId: sessionId,
  };
  const params: Record<string, string> = {};
  const headers: Record<string, string> = getContactSupportRequestHeaders(store.getState);
  addAppNamesToParam(params, appState.GlobalState.appVer, appState.GlobalState.sourceApp);
  addExpGuidToParam(params);
  const config: AxiosRequestConfig = {
    params: params,
  };
  try {
    return await httpUtils.post<GetHelpSMCGetUserLicensesResponse>(destinationUrl, data, headers, config);
  } catch (error) {
    logCustomError(
      `getUserLicensesApi - error caught, errorMessage: ${error.message}, statusCode: ${error.response?.status}`
    );
    throw error;
  }
};
